import React, { useRef, useState } from 'react'
import { Menu } from 'primereact/menu'
import { FaEllipsisV, FaExternalLinkAlt, FaPrint } from 'react-icons/fa'
import { CgSpinner } from 'react-icons/cg'
import { MdOutlineSick } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ReusableModal from '../../../shared/ReuseableModal'
import CheckPermission from '../../../hooks/CheckPermission'
import { fetchData } from '../../../helpers/fetchData'
import ViewSickeLeaveReport from '../../../pages/HIS/patient-profiling/ViewSickeLeaveReport'
import CreateSickLeaveForm from '../../../pages/HIS/patient-profiling/CreateSickLeaveForm'
import CreateReferralForm from '../../../pages/HIS/patient-profiling/CreateReferralForm'
import CreateExternalReferralForm from '../../../pages/HIS/patient-profiling/CreateExternalReferralForm'
import ViewReferralFormReport from '../../../pages/HIS/patient-profiling/ViewReferralFormReport'
import ViewExternalReferralFormReport from '../../../pages/HIS/patient-profiling/ViewExternalReferralFormReport'

const actionButtonClass =
  'w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-[6px] transition-all border'

const VisitCreateActionsMenu = ({
  visitData,
  onEditEmrItems,
  printActions = false
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = CheckPermission()
  const [loading, setLoading] = useState(false)
  const [createActionEmrData, setCreateActionEmrData] = useState(null)
  const [openCreateSickLeaveModal, setOpenCreateSickLeaveModal] =
    useState(false)
  const [openCreateReferralModal, setOpenCreateReferralModal] = useState(false)
  const [openCreateExternalReferralModal, setOpenCreateExternalReferralModal] =
    useState(false)
  const [selectedReferralData, setSelectedReferralData] = useState(null)
  const [openReferralPrintModal, setOpenReferralPrintModal] = useState(false)
  const [openExternalReferralPrintModal, setOpenExternalReferralPrintModal] =
    useState(false)
  const [selectedSickLeaveData, setSelectedSickLeaveData] = useState(null)
  const [openSickLeavePrintModal, setOpenSickLeavePrintModal] = useState(false)
  const menuRef = useRef(null)

  const hasEmr = visitData?.emr_numbers?.length > 0

  const fetchPrimaryEmrData = async () => {
    const emrNumber = visitData?.emr_numbers?.[0]
    if (!emrNumber) return null

    setLoading(true)
    try {
      const payload = {
        emr_id: Number(emrNumber),
        visit_id: visitData?.visit_id,
        patient_id: visitData?.patient?.patient_id
      }
      const response = await fetchData('POST', '/v3/emr/getById', payload)
      return response?.data ? { ...response.data, ...payload } : null
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSickLeave = async () => {
    const emrData = await fetchPrimaryEmrData()
    if (!emrData) return
    setCreateActionEmrData(emrData)
    setOpenCreateSickLeaveModal(true)
  }

  const handleCreateReferral = async external => {
    const emrData = await fetchPrimaryEmrData()
    if (!emrData) return
    setCreateActionEmrData(emrData)
    if (external) {
      setOpenCreateExternalReferralModal(true)
    } else {
      setOpenCreateReferralModal(true)
    }
  }

  const handleCreateAdmission = async () => {
    const emrData = await fetchPrimaryEmrData()
    if (!emrData) return
    navigate('/patient-profile/create-admission', {
      state: { selectedEMRData: emrData }
    })
  }

  const handleViewSickLeave = async () => {
    setLoading(true)
    try {
      const res = await fetchData('POST', '/v3/leave/getList', {
        page: 1,
        per_page: 10,
        filters: {
          'patient.patient_id': visitData?.patient?.patient_id
        }
      })
      if (res?.data?.length > 0) {
        setSelectedSickLeaveData(res.data[res.data.length - 1])
        setOpenSickLeavePrintModal(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePrintReferral = async external => {
    const emrData = await fetchPrimaryEmrData()
    if (!emrData) return
    setLoading(true)
    try {
      const res = await fetchData('POST', '/v3/referral-form/getList', {
        page: 1,
        per_page: 20,
        filters: {
          emr_id: String(emrData?.emr_id || '')
        }
      })
      const referral = res?.data?.find(item =>
        external ? item?.form_type === 'external' : item?.form_type !== 'external'
      )
      if (referral) {
        setSelectedReferralData(referral)
        external
          ? setOpenExternalReferralPrintModal(true)
          : setOpenReferralPrintModal(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleEditEmrItems = () => {
    onEditEmrItems?.(visitData?.emr_numbers?.[0])
  }

  const printOptions = [
    ...(hasEmr && hasPermission('leave', 'get')
      ? [
          {
            label: t('View Sick Leave'),
            icon: <MdOutlineSick size={18} />,
            onClick: handleViewSickLeave
          }
        ]
      : []),
    ...(hasEmr && hasPermission('referral_form', 'get')
      ? [
          {
            label: t('Print Internal Referral'),
            icon: <FaPrint size={15} />,
            onClick: () => handlePrintReferral(false)
          },
          {
            label: t('Print External Referral'),
            icon: <FaExternalLinkAlt size={14} />,
            onClick: () => handlePrintReferral(true)
          }
        ]
      : [])
  ]

  const options = [
    ...(hasEmr && hasPermission('leave', 'create')
      ? [{ label: t('Create Sick Leave'), onClick: handleCreateSickLeave }]
      : []),
    ...(hasEmr && hasPermission('referral_form', 'create')
      ? [
          { label: t('Create Internal Referral'), onClick: () => handleCreateReferral(false) },
          { label: t('Create External Referral'), onClick: () => handleCreateReferral(true) }
        ]
      : []),
    ...(hasEmr && hasPermission('emr', 'update') && onEditEmrItems
      ? [{ label: t('Edit EMR Items'), onClick: handleEditEmrItems }]
      : []),
    ...(hasEmr && (hasPermission('patientAdmission', 'create') || hasPermission('encounter_admission', 'create'))
      ? [{ label: t('Create Admission Request'), onClick: handleCreateAdmission }]
      : [])
  ]

  if (!options.length && (!printActions || !printOptions.length)) return null

  const menuId = `visit_create_actions_menu_${
    visitData?.visit_id ||
    visitData?.id ||
    visitData?._id ||
    visitData?.emr_numbers?.[0] ||
    'default'
  }`

  const menuItems = options.map((option, index) => ({
    label: option.label,
    template: item => (
      <button
        type='button'
        className={`group flex min-h-[34px] w-full items-center justify-between whitespace-nowrap px-3.5 py-2 text-left text-[11px] md:text-[12px] font-semibold text-gray-700 transition-colors hover:bg-blue/5 hover:text-blue focus:outline-none focus:bg-blue/5 focus:text-blue active:bg-blue/10 ${
          index < options.length - 1 ? 'border-b border-gray-100' : ''
        }`}
        onClick={event => {
          menuRef.current?.hide(event)
          option.onClick()
        }}
      >
        {item.label}
      </button>
    )
  }))

  return (
    <>
      {printActions ? (
        <div className='flex items-center gap-1.5'>
          {printOptions.map(option => (
            <button
              key={option.label}
              type='button'
              className={`${actionButtonClass} ${
                loading
                  ? 'opacity-50 cursor-not-allowed border-gray-200'
                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-blue/10 hover:text-blue hover:border-blue/20'
              }`}
              disabled={loading}
              title={option.label}
              onClick={option.onClick}
            >
              {loading ? (
                <CgSpinner size={20} className='animate-spin text-blue' />
              ) : (
                option.icon
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className='relative'>
          <button
            type='button'
            className={`${actionButtonClass} ${
              loading
                ? 'opacity-50 cursor-not-allowed border-gray-200'
                : 'bg-white/80 text-gray-700 border-white/70 hover:bg-white hover:text-blue hover:border-blue/20'
            }`}
            disabled={loading}
            title={t('Create')}
            aria-controls={menuId}
            aria-haspopup
            onClick={event => menuRef.current?.toggle(event)}
          >
            {loading ? (
              <CgSpinner size={20} className='animate-spin text-blue' />
            ) : (
              <FaEllipsisV size={16} />
            )}
          </button>
          <Menu
            model={menuItems}
            popup
            ref={menuRef}
            id={menuId}
            popupAlignment='right'
            className='custom-menu !mt-2 !min-w-[220px] !overflow-hidden !rounded-[8px] !border !border-gray-200 !bg-white !p-0 !shadow-[0_14px_36px_rgba(15,23,42,0.14)] !ring-1 !ring-black/5'
            pt={{
              root: { className: '!p-0' },
              menu: { className: '!m-0 !p-0' },
              menuitem: { className: '!p-0' },
              content: { className: '!rounded-none !bg-white' },
              action: { className: '!p-0 !bg-transparent' }
            }}
          />
        </div>
      )}

      <ReusableModal
        className='w-full md:w-[700px] lg:w-[800px] relative'
        isVisible={openCreateSickLeaveModal}
        title={t('Create Sick Leave')}
        position='top'
        onClose={() => {
          setOpenCreateSickLeaveModal(false)
          setCreateActionEmrData(null)
        }}
      >
        <CreateSickLeaveForm
          emrData={createActionEmrData}
          patientData={visitData?.patient}
          setOpenCreateSickLeaveModal={setOpenCreateSickLeaveModal}
          setSelectEmrData={setCreateActionEmrData}
        />
      </ReusableModal>

      <ReusableModal
        className='w-full md:w-[800px] lg:w-[1000px] h-[90vh] relative'
        isVisible={openSickLeavePrintModal}
        title={t('Sick Leave Report')}
        position='top'
        onClose={() => {
          setOpenSickLeavePrintModal(false)
          setSelectedSickLeaveData(null)
        }}
      >
        <ViewSickeLeaveReport selectedLeaveData={selectedSickLeaveData} />
      </ReusableModal>

      <ReusableModal
        className='w-full md:w-[800px] lg:w-[950px] relative'
        isVisible={openCreateReferralModal}
        title={t('Create Referral Form')}
        position='top'
        onClose={() => {
          setOpenCreateReferralModal(false)
          setCreateActionEmrData(null)
        }}
      >
        <CreateReferralForm
          emrData={createActionEmrData}
          patientData={visitData?.patient}
          setOpenReferralFormModal={setOpenCreateReferralModal}
          setSelectEmrData={setCreateActionEmrData}
        />
      </ReusableModal>

      <ReusableModal
        className='w-full md:w-[800px] lg:w-[950px] relative'
        isVisible={openCreateExternalReferralModal}
        title={t('Create External Referral Form')}
        position='top'
        onClose={() => {
          setOpenCreateExternalReferralModal(false)
          setCreateActionEmrData(null)
        }}
      >
        <CreateExternalReferralForm
          emrData={createActionEmrData}
          patientData={visitData?.patient}
          setOpenExternalReferralFormModal={setOpenCreateExternalReferralModal}
          setSelectEmrData={setCreateActionEmrData}
        />
      </ReusableModal>

      <ReusableModal
        className='w-full md:w-[850px] lg:w-[1050px] h-[90vh] relative'
        isVisible={openReferralPrintModal}
        title={t('Referral Form')}
        position='top'
        onClose={() => {
          setOpenReferralPrintModal(false)
          setSelectedReferralData(null)
        }}
      >
        <ViewReferralFormReport selectedReferralData={selectedReferralData} />
      </ReusableModal>

      <ReusableModal
        className='w-full md:w-[850px] lg:w-[1050px] h-[90vh] relative'
        isVisible={openExternalReferralPrintModal}
        title={t('External Referral Form')}
        position='top'
        onClose={() => {
          setOpenExternalReferralPrintModal(false)
          setSelectedReferralData(null)
        }}
      >
        <ViewExternalReferralFormReport
          selectedReferralData={selectedReferralData}
        />
      </ReusableModal>
    </>
  )
}

export default VisitCreateActionsMenu
